/**
 * The **zod schema for an authored {@link OverlayEntry}** — the *one hard gate*. An author
 * agent returns structured output; the orchestrator forces it through
 * {@link parseOverlayEntry} before it is ever written. A malformed entry is **rejected**
 * (retry, then drop to a gap) — never a corrupt write, and no human is ever queued for
 * something that doesn't parse.
 *
 * **This schema is the runtime mirror of the compile-time {@link EffectAtom}/{@link Predicate}/
 * {@link EffectEntry} unions in `atoms.ts`/`predicate.ts`.** It necessarily *enumerates the atom
 * ops* (the discriminated-union arms), so it is designed **additively** (one arm per op) — a new
 * op is a new arm, not a reshape. {@link SCHEMA_ATOM_OPS} is the schema's op list;
 * `schema.test.ts` asserts it set-equals `IMPLEMENTED_ATOM_OPS` (the interpreter's op list) — so
 * the day a new atom op lands in the engine vocabulary, that parity test fails **loudly +
 * located**, telling whoever added it to add the matching schema arm.
 *
 * **It validates the *authored / on-disk* shape, which is a superset of the bound runtime type.**
 * A numeric magnitude may be authored as a `{VAL}`/`{VAL:key}` **template placeholder string**
 * (the loader binds it per-weapon at gather, `loader.ts`) — e.g. Accurate `{VAL}`, Knockback
 * `{VAL}`. So magnitude slots accept {@link NumberOrTemplate} (a number *or* a well-formed
 * placeholder), nothing else. Structural discriminants (`op`/`kind`/enums) stay exact.
 *
 * **Strictness:** every object is `.strict()` — an authored entry carrying an *unknown* key is
 * rejected, not silently dropped. The overlay format is entirely ours (no external rich content
 * to passthrough, unlike the LCP schemas in `@lancer-vtt/compendium`), so a strict gate is the
 * stronger wall an LLM author should hit when it hallucinates a field.
 *
 * **STANDING CONSTRAINT — read this before adding `.default()`/`.transform()`/`z.coerce`/`.catch()`/
 * `.preprocess()` anywhere in this file.**
 * `sidecar.ts`'s `parseSidecar` (the boot rail's per-entry gate) pushes zod's *parsed OUTPUT*
 * (`gate.data` from {@link parseOverlayEntry}) into `ParsedSidecar.entries` — not the raw JSON it
 * read off disk. Today that is safe **only** because every schema in this file is `.strict()` and
 * none of the five value-rewriting zod combinators above appears anywhere in it: parsing is a
 * pure narrowing/rejection, so `gate.data` is always structurally identical to the input it
 * validated. **The day any of those combinators is added — a coerced number, a defaulted field, a
 * `.catch()` fallback — the boot rail starts silently REWRITING shipped content on load**: every
 * server, every browser tab, and the world-sidecar auto-sync's validate-before-overwrite gate
 * (which also calls `parseSidecar` on the incoming pack) would carry the rewrite forward with zero
 * signal. If you need one of these combinators, do the narrowing/defaulting
 * *before* the value is treated as canonical (e.g. in the authoring pipeline, before export), not
 * inside this on-disk-shape schema. See `sidecar.test.ts`'s `describe("parse — schema output
 * equals raw input …")` for the regression pin (converts this doc constraint into a failing test).
 *
 * Pure (no `fs`) — it lives in `@lancer-vtt/rules` next to the types it validates, importable by
 * the orchestrator, tests, and (eventually) any review surface. The recursive schemas are typed
 * `z.ZodTypeAny` (TS cannot infer a recursive zod type); the typed result comes back through
 * {@link parseOverlayEntry}, which casts the runtime-validated value to {@link OverlayEntry}.
 *
 * **The `triggered` clause arm (and the `modifier` arm's `match`) validate ONLY the native
 * `on`/`match` shape** (`atoms.ts`'s `TriggeredEffect`/`ModifierEffect`) — every pack entry
 * + seed entry ships this shape.
 */

import { z } from "zod";
import { ATTACK_VERBS, EARMARK_VERBS, TILE_TYPE_IDS, type ObjectProfile, type TileTypeId } from "@lancer-vtt/domain";
import type { EffectAtom, OverlayEntry } from "./atoms";
import type { Predicate } from "./predicate";

// ── the `{VAL}` template placeholder (mirrors loader.ts) ──────────────────────────────

/** The placeholder a templated numeric slot carries (`{VAL}` / `{VAL:key}`) — kept in sync with `loader.ts`. */
const PLACEHOLDER = /^\{VAL(?::[A-Za-z0-9_]+)?\}$/;

/** A numeric magnitude OR a `{VAL}` template placeholder string (the loader binds it). */
const NumberOrTemplate = z.union([z.number(), z.string().regex(PLACEHOLDER)]);

// ── shared closed enums (mirrored from the TS unions) ─────────────────────────────────

/** The comparison operators a `resource`/`stat` predicate or a `roll` atom may use ({@link CmpOp}). */
const CmpOpSchema = z.enum([">=", ">", "<=", "<", "=="]);

/**
 * A symbolic actor reference ({@link AtomActorRef}/`PredicateActor`). `self`/`target`/`attacker`
 * are the named refs; a raw combatant id also resolves — so the runtime shape is any non-empty
 * string.
 */
const ActorRefSchema = z.string().min(1);
/** The cadence cap the `use_cap` atom spends and the `cap_open` predicate reads (`ResourceCap`). */
const CapSpecSchema = z.object({ key: z.string().min(1), max: z.number().int().positive(), per: z.enum(["round", "own_turn", "turn", "scene"]).optional() }).strict();

/**
 * A {@link import("./atoms").StatScaledAmount} — the authored shape of a magnitude that reads a
 * live combatant stat instead of a bound number (`ms_type_i_flight_system`'s "Size +1 Heat";
 * `"maxHp"` covers `heal`'s stat-relative amount, e.g. pg_patch's "half their maximum HP";
 * `"speed"` (`npc-features-fielded-roster` Phase 3.3) covers a granted movement action's
 * Speed-scaled distance). A
 * fractional result rounds UP — Golden Rule II — see the type's own doc in `atoms.ts`.
 */
const StatScaledAmountSchema = z
  .object({
    stat: z.enum(["size", "maxHp", "speed"]),
    who: ActorRefSchema.optional(),
    mult: z.number().optional(),
    add: z.number().optional(),
  })
  .strict();

/**
 * A {@link import("./atoms").TierScaledAmount} (`npc-features-fielded-roster` Phase 3.4,
 * demand (d)) — the authored shape of a magnitude that scales off the carrier's NPC
 * deployment tier, RAW's own "{2/3/4}" bracket notation. `tier` is the closed 3-tuple
 * `[T1, T2, T3]`. See the type's own doc in `atoms.ts` for the no-frozen-tier degrade.
 */
const TierScaledAmountSchema = z
  .object({
    tier: z.tuple([z.number(), z.number(), z.number()]),
    who: ActorRefSchema.optional(),
  })
  .strict();

/**
 * A {@link import("./atoms").TargetCountScaledAmount} — the `{TARGET_COUNT}` binding's authored
 * shape (`npc-features-fielded-roster`, the area fan-out tock). `targetCount` is the closed
 * two-value counting rule (`"all"` | `"beyond_first"`), NOT a free string: RAW prints exactly
 * these two readings and a typo (`"beyondFirst"`) would otherwise sail through and then scale
 * by the wrong count forever. `mult` accepts a plain number OR a
 * {@link TierScaledAmountSchema} — Cluster Munitions' "+{2/3/4} for each target beyond the
 * first" is the product of the two bindings — and deliberately nests exactly ONE level (never
 * the full `MagnitudeOrTemplate`, which would admit a recursive or stat-scaled multiplier
 * nothing resolves).
 */
const TargetCountScaledAmountSchema = z
  .object({
    targetCount: z.enum(["all", "beyond_first"]),
    mult: z.union([NumberOrTemplate, TierScaledAmountSchema]).optional(),
    add: NumberOrTemplate.optional(),
  })
  .strict();

/** A {@link import("./atoms").RolledAmount}: an enclosing roll's bound total, scaled. */
const RolledAmountSchema = z
  .object({
    rolled: z.string(),
    mult: z.number().optional(),
    add: z.number().optional(),
  })
  .strict();

/** A {@link import("./atoms").MovedSpacesAmount}: the firing move's spaces, scaled. */
const MovedSpacesAmountSchema = z
  .object({
    moved: z.literal("spaces"),
    mult: z.number().optional(),
    add: z.number().optional(),
  })
  .strict();

/** A {@link NumberOrTemplate} magnitude OR a {@link StatScaledAmountSchema}/{@link TierScaledAmountSchema}/{@link TargetCountScaledAmountSchema}/{@link RolledAmountSchema} (a `MagnitudeValue`). */
const MagnitudeOrTemplate = z.union([NumberOrTemplate, StatScaledAmountSchema, TierScaledAmountSchema, TargetCountScaledAmountSchema, RolledAmountSchema, MovedSpacesAmountSchema]);

/**
 * The object-shaped {@link import("@lancer-vtt/domain").StatusExpiry} — mirrors `types.ts`
 * exactly (one arm per `kind`; `turn`'s `edge`/`who` are its two 2-valued axes).
 */
const StatusExpirySchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("save_ends"), stat: z.string().optional() }).strict(),
  z.object({ kind: z.literal("end_of_round") }).strict(),
  z.object({ kind: z.literal("until_removed") }).strict(),
  z.object({ kind: z.literal("scene") }).strict(),
  z.object({ kind: z.literal("movement") }).strict(),
  z.object({ kind: z.literal("this_turn") }).strict(),
  z.object({ kind: z.literal("while_status"), status: z.string().min(1) }).strict(),
  z.object({ kind: z.literal("turn"), edge: z.enum(["start", "end"]), who: z.enum(["target", "source", "partner"]) }).strict(),
]);

/** Damage types an `extra_damage` atom may deal. */
const DamageTypeSchema = z.enum(["kinetic", "energy", "explosive", "burn", "heat", "variable"]);

/**
 * The closed **capability** vocabulary a `has_capability` predicate names — mirrors
 * {@link CAPABILITY_PREDICATES} in `predicate.ts`, the ONE code-side source of truth each key
 * delegates to. Adding a new capability (e.g. `sensors`) is additive on both sides — a new enum
 * member here + a new map entry there, never a schema reshape.
 */
const CapabilitySchema = z.enum(["heatTrack", "structureTrack", "stressTrack", "terrain", "downAndOut", "character"]);

// ── the event-family model ────────────────────────────────────────────────────────────

/**
 * The closed family taxonomy ({@link import("./family").TriggerFamily}) — the `on` a
 * `triggered`/`modifier` clause carries.
 */
const TriggerFamilySchema = z.enum([
  "attack",
  "damage",
  "heat",
  "track_damage",
  "destruction",
  "status",
  "save",
  "action",
  "move",
  "trample",
  "aura",
  "start_turn",
  "end_turn",
  "start_round",
  "end_round",
]);

/** A {@link import("./family").MatchScalar} — a literal number OR a one-key {@link CmpOp} comparator object. */
const MatchScalarSchema = z.union([
  z.number(),
  z
    .object({
      ">=": z.number().optional(),
      ">": z.number().optional(),
      "<=": z.number().optional(),
      "<": z.number().optional(),
      "==": z.number().optional(),
    })
    .strict(),
]);

/** A {@link import("./family").MatchStringField} — a literal string OR an any-of array. */
const MatchStringFieldSchema = z.union([z.string(), z.array(z.string())]);

/**
 * {@link import("./family").AttackMatch.cover} — the same literal-or-any-of-array shape as
 * {@link MatchStringFieldSchema}, but narrowed to the three real {@link
 * import("./predicate").CoverDegree} values instead of an open string. Deliberately narrower
 * than its TS counterpart's structural width buys: a typo (`cover: "None"`, `cover: "full"`)
 * would typecheck nowhere but would sail through an open-string schema and then silently never
 * match at fire time — the exact never-fires-and-says-nothing failure a `match` schema exists to
 * catch at the authoring gate.
 */
const CoverMatchFieldSchema = z.union([
  z.enum(["none", "soft", "hard"]),
  z.array(z.enum(["none", "soft", "hard"])),
]);

/**
 * An attack verb ({@link import("./family").AttackMatch.verb}, `attackScope.verb`): a CLOSED enum
 * of the attack-verb space (`ATTACK_VERBS`), not a free string. As a free string, a typo
 * (`"skirmisch"`) or a verb no attack presents as (`"barrage"`) parsed clean and silently never
 * matched, the same failure the `EARMARK_VERBS` enum below closes for earmarks. The two verb
 * enums are different sets and stay separate (see `ATTACK_VERBS`' doc).
 */
const AttackVerbSchema = z.enum(ATTACK_VERBS);

/**
 * The per-family `match` patterns ({@link import("./family").TriggerMatch}) — a discriminated
 * union on `family`, mirroring `family.ts` field-for-field. Every arm is `.strict()` so a
 * wrong-family field (e.g. `hit` on a `turn` match) is rejected, not silently ignored.
 */
const AttackMatchSchema = z
  .object({
    family: z.literal("attack"),
    phase: z.enum(["declared", "resolved", "after"]).optional(),
    hostileActions: z.literal(true).optional(),
    hit: z.boolean().optional(),
    crit: z.boolean().optional(),
    ranged: z.boolean().optional(),
    tech: z.boolean().optional(),
    verb: z.union([AttackVerbSchema, z.array(AttackVerbSchema)]).optional(),
    weaponType: MatchStringFieldSchema.optional(),
    mount: MatchStringFieldSchema.optional(),
    firstAttackThisTurn: z.boolean().optional(),
    natural20: z.boolean().optional(),
    rawD20: MatchScalarSchema.optional(),
    actionId: MatchStringFieldSchema.optional(),
    cover: CoverMatchFieldSchema.optional(),
    // The area fan-out's settled target count ({@link import("./family").AttackMatch.targetCount})
    // — a `MatchScalar`, so `{ ">": 1 }` reads "this attack targets more than one character."
    targetCount: MatchScalarSchema.optional(),
    // A status this attack consumes ({@link import("./family").AttackMatch.consumes}).
    consumes: z.string().min(1).optional(),
    // The named reaction this attack spends ({@link import("./family").AttackMatch.reaction}).
    reaction: MatchStringFieldSchema.optional(),
  })
  .strict();
const DamageMatchSchema = z.object({ family: z.literal("damage"), ranged: z.boolean().optional(), tech: z.boolean().optional(), hit: z.boolean().optional(), dealt: z.boolean().optional(), dtype: DamageTypeSchema.optional() }).strict();
const HeatMatchSchema = z.object({ family: z.literal("heat"), dangerZone: z.boolean().optional(), cleared: z.boolean().optional() }).strict();
const TrackDamageMatchSchema = z
  .object({ family: z.literal("track_damage"), track: z.enum(["structure", "stress"]).optional() })
  .strict();
const DestructionMatchSchema = z.object({ family: z.literal("destruction") }).strict();
const StatusMatchSchema = z
  .object({ family: z.literal("status"), change: z.enum(["gained", "lost"]).optional(), status: MatchStringFieldSchema.optional(), reason: z.enum(["expired", "removed"]).optional() })
  .strict();
const SaveMatchSchema = z
  .object({
    family: z.literal("save"),
    phase: z.enum(["declared", "resolved"]).optional(),
    stat: MatchStringFieldSchema.optional(),
    cause: MatchStringFieldSchema.optional(),
    dc: MatchScalarSchema.optional(),
  })
  .strict();
const ActionMatchSchema = z.object({ family: z.literal("action"), actionId: MatchStringFieldSchema.optional(), verb: z.enum(EARMARK_VERBS).optional() }).strict();
const MoveMatchSchema = z.object({ family: z.literal("move"), distance: MatchScalarSchema.optional(), voluntary: z.boolean().optional(), phase: z.literal("declared").optional() }).strict();
/** {@link import("./family").TrampleMatch} — no fields today; a clause omits `match` entirely rather than authoring an empty one. */
const TrampleMatchSchema = z.object({ family: z.literal("trample") }).strict();
/** {@link import("./family").AuraMatch}: the moment, or an any-of list of moments. */
const AuraMomentSchema = z.enum(["turn_start", "enter", "arrive", "turn_end", "before_attack"]);
const AuraMatchSchema = z.object({ family: z.literal("aura"), moment: z.union([AuraMomentSchema, z.array(AuraMomentSchema).min(1)]) }).strict();
/** The four lifecycle families carry no match fields (the edge is the family name); a clause omits `match`. */
const StartTurnMatchSchema = z.object({ family: z.literal("start_turn") }).strict();
const EndTurnMatchSchema = z.object({ family: z.literal("end_turn") }).strict();
const StartRoundMatchSchema = z.object({ family: z.literal("start_round") }).strict();
const EndRoundMatchSchema = z.object({ family: z.literal("end_round") }).strict();

/** The {@link import("./family").TriggerMatch} schema — a `triggered` clause's `match`. */
const TriggerMatchSchema = z.discriminatedUnion("family", [
  AttackMatchSchema,
  DamageMatchSchema,
  HeatMatchSchema,
  TrackDamageMatchSchema,
  DestructionMatchSchema,
  StatusMatchSchema,
  SaveMatchSchema,
  ActionMatchSchema,
  MoveMatchSchema,
  TrampleMatchSchema,
  AuraMatchSchema,
  StartTurnMatchSchema,
  EndTurnMatchSchema,
  StartRoundMatchSchema,
  EndRoundMatchSchema,
]);

// ── the predicate language (closed, composable, recursive) ────────────────────────────

/** The {@link Predicate} schema (recursive via `and`/`or`/`not`). */
export const PredicateSchema: z.ZodTypeAny = z.lazy(() =>
  z.discriminatedUnion("op", [
    z.object({ op: z.literal("and"), preds: z.array(PredicateSchema) }).strict(),
    z.object({ op: z.literal("or"), preds: z.array(PredicateSchema) }).strict(),
    z.object({ op: z.literal("not"), pred: PredicateSchema }).strict(),
    // `source` narrows to instances with a matching source: the firing clause's carrier
    // (`"carrier"`, lint rule 4 limits where), an authored literal (`{ id }`, paired with
    // `AddStatusAtom.sourceOverride`), or a bound actor (`{ actor }`). See `StatusSourceSpec`.
    z
      .object({
        op: z.literal("has_status"),
        who: ActorRefSchema,
        status: z.string(),
        source: z
          .union([z.literal("carrier"), z.object({ id: z.string().min(1) }).strict(), z.object({ actor: ActorRefSchema }).strict()])
          .optional(),
        scope: z.literal("any_live").optional(),
        appliedBefore: z.literal("this_turn").optional(),
      })
      .strict(),
    // The sanctioned replacement for the `stat maxHeat`-style null-sentinel workaround:
    // dispatches on CAPABILITY PRESENCE, never on `kind`.
    z
      .object({ op: z.literal("has_capability"), who: ActorRefSchema, capability: CapabilitySchema })
      .strict(),
    z
      .object({ op: z.literal("resource"), who: ActorRefSchema, key: z.string(), cmp: CmpOpSchema, value: NumberOrTemplate })
      .strict(),
    z.object({ op: z.literal("cap_open"), who: ActorRefSchema, cap: CapSpecSchema, on: ActorRefSchema.optional() }).strict(),
    z
      .object({ op: z.literal("limited_charges"), who: ActorRefSchema, items: z.array(z.string().min(1)).min(1), cmp: CmpOpSchema, value: z.number().int() })
      .strict(),
    z
      .object({
        op: z.literal("stat"),
        who: ActorRefSchema,
        stat: z.enum(["hp", "heat", "armor", "evasion", "edef", "speed", "saveTarget", "maxHp", "maxHeat", "size"]),
        cmp: CmpOpSchema,
        value: NumberOrTemplate,
      })
      .strict(),
    // A yes/no fact the GM rules on, for a condition the engine doesn't model (t_tactician:2's
    // "higher elevation than your target"). `prompt` is the RAW-worded question; `id` is scoped
    // per entry at load. Never for a positional fact the map answers: use a geometry op.
    z.object({ op: z.literal("gm_ruling"), id: z.string().min(1), prompt: z.string().min(1) }).strict(),
    // A clause gated on the referenced combatant holding a frozen effect id (the Bondmate
    // status's Intercede requires the protector's `t_bonded:2`).
    z.object({ op: z.literal("has_effect_id"), who: ActorRefSchema, id: z.string().min(1) }).strict(),
    // The identity predicates (Type-3 Projected Shield).
    z.object({ op: z.literal("attacker_is"), who: ActorRefSchema }).strict(),
    z.object({ op: z.literal("target_is"), who: ActorRefSchema }).strict(),
    // t_siege_specialist:1's "If you have a Cannon" loadout-eligibility gate.
    z.object({ op: z.literal("loadout_has_weapon_type"), who: ActorRefSchema, value: z.string() }).strict(),
    // tactician's existential ally-spatial gate.
    z
      .object({
        op: z.literal("exists_ally"),
        who: ActorRefSchema,
        rel: z.enum(["adjacent", "range"]),
        relativeTo: ActorRefSchema,
        param: NumberOrTemplate.optional(),
        among: z.enum(["ally", "character"]).optional(),
      })
      .strict(),
    // nuclear_cavalier's Danger Zone (heat >= maxHeat/2) gate.
    z.object({ op: z.literal("danger_zone"), who: ActorRefSchema }).strict(),
    // duelist/gunslinger/exemplar/nuclear_cavalier's "on your turn" gate.
    z.object({ op: z.literal("on_turn"), who: ActorRefSchema }).strict(),
    // spotter's Panopticon "if you did not move" gate (the turn's spend counter).
    z.object({ op: z.literal("moved_this_turn"), who: ActorRefSchema }).strict(),
    z.object({ op: z.literal("acted_this_turn"), who: ActorRefSchema }).strict(),
    // juggernaut's Kinetic Mass Transfer: a push this resolution stopped short against a character or an obstruction.
    z.object({ op: z.literal("collided"), who: ActorRefSchema, with: z.enum(["character", "obstruction"]) }).strict(),
    // ms_eva_module's zero-g/underwater environment gate. `value` mirrors
    // `@lancer-vtt/domain`'s closed `EncounterEnvironment` union verbatim.
    z.object({ op: z.literal("scene_environment"), value: z.enum(["zero_g", "underwater"]) }).strict(),
    // ms_stasis_generator/ms_stay_of_execution's hostile-vs-willing-ally save-scoping gate (a
    // pairwise sideOf comparison, `exists_ally`'s internal same-side check exposed as its own arm).
    z.object({ op: z.literal("hostile_to"), who: ActorRefSchema, relativeTo: ActorRefSchema }).strict(),
    // t_bonded:1's "while you are adjacent [to your Bondmate]" self-gate: a pairwise
    // LIVE-geometry adjacency check (real map distance), most useful as
    // `who:"self", relativeTo:"partner"`.
    z.object({ op: z.literal("adjacent_to"), who: ActorRefSchema, relativeTo: ActorRefSchema }).strict(),
    // Electropulse's "adjacent to any Drone or Deployable": a live drone, deployable or system-deployed piece; never a mine.
    z.object({ op: z.literal("adjacent_to_deployed"), who: ActorRefSchema }).strict(),
    // ms_beckoner's Beckon: "the same Size as you or larger" (a pairwise Size compare), and its
    // swap-legality gate (both footprints land legally on the other's anchor).
    z.object({ op: z.literal("size_vs"), who: ActorRefSchema, cmp: CmpOpSchema, relativeTo: ActorRefSchema }).strict(),
    z.object({ op: z.literal("can_swap"), who: ActorRefSchema, relativeTo: ActorRefSchema }).strict(),
    // ms_type_3_projected_shield's "Nominate a character within line of sight" (no range to
    // freeze, so the activate reach wall can't gate it): a live-geometry sightline check.
    z.object({ op: z.literal("line_of_sight"), who: ActorRefSchema, relativeTo: ActorRefSchema }).strict(),
    // Siege Armor's "beyond range 3" gate (`npc-features-fielded-roster` Phase 2,
    // `decisions/0171`): a pairwise LIVE-geometry range-band check, `min`/`max` inclusive.
    z
      .object({ op: z.literal("range_to"), who: ActorRefSchema, relativeTo: ActorRefSchema, max: z.union([z.number(), z.literal("sensors")]).optional(), min: z.number().optional() })
      .strict(),
    // Point-Defense Shield's "the closest hostile character; no effect on a tie" gate (same
    // Phase 2/decision). `unique: true` is PDS's own RAW shape.
    z
      .object({ op: z.literal("is_closest_hostile"), who: ActorRefSchema, relativeTo: ActorRefSchema, unique: z.boolean() })
      .strict(),
    // Blinkfield Intervention's "within the affected area": `who` is inside a live area `source` made.
    z
      .object({ op: z.literal("in_area"), who: ActorRefSchema, source: ActorRefSchema, grants: z.enum(["soft_cover", "outside_attack_difficulty", "no_reactions"]).optional() })
      .strict(),
    // The deploy/recall toggle gate (is `who`'s `groupId` currently live?).
    z.object({ op: z.literal("object_group_deployed"), who: ActorRefSchema, groupId: z.string().min(1), live: z.literal(true).optional() }).strict(),
    // The resolution-scoped fact ledger's `choose`-atom read (t_walking_armory:1's ammo pick
    // correlating to a same-attack damage-type/rider gate).
    z.object({ op: z.literal("chose"), key: z.string().min(1), value: z.string().min(1) }).strict(),
  ]),
);

/**
 * The predicate ops {@link PredicateSchema} accepts, read off its own discriminated union rather
 * than kept as a copy. `coverage.ts`'s `IMPLEMENTED_PREDICATE_OPS` is this list, so adding an op
 * means adding its arm above and nothing in coverage (`decisions/0183`).
 */
export const SCHEMA_PREDICATE_OPS = [
  ...(PredicateSchema as z.ZodLazy<z.ZodDiscriminatedUnion<"op", z.ZodDiscriminatedUnionOption<"op">[]>>).schema.optionsMap.keys(),
] as Predicate["op"][];

// ── the area selector (for `for_each`) ────────────────────────────────────────────────

const AreaSelectorSchema: z.ZodTypeAny = z.lazy(() =>
  z
    .object({
      shape: z.enum(["disk", "line", "cone"]),
      size: NumberOrTemplate,
      origin: z.enum(["self", "target", "site", "own_drones"]),
      select: z.enum(["ally", "enemy", "character", "object", "character_or_object", "drone"]),
      where: PredicateSchema.optional(),
      exclude_origin: z.boolean().optional(),
      exclude_target: z.boolean().optional(),
      size_from: z.enum(["weapon_threat", "sensors"]).optional(),
      collided: z.boolean().optional(),
      passed_through: z.boolean().optional(),
      first_reached: z.literal(true).optional(),
    })
    .strict()
    .refine((sel) => sel.origin !== "own_drones" || sel.shape === "disk", { message: 'origin "own_drones" is a union of disks; shape must be "disk"' }),
);

// ── the object profile (deploy_object's spatial-contribution facet) ──────────────────

/** Mirrors `@lancer-vtt/domain`'s `ObjectProfile` exactly. */
const ObjectProfileSchema = z
  .object({
    blocksMovement: z.boolean().optional(),
    blocksFlight: z.boolean().optional(),
    blocksSight: z.boolean().optional(),
    cover: z.enum(["none", "soft", "hard"]).optional(),
    moveCost: NumberOrTemplate.optional(),
    door: z.literal(true).optional(),
    rooted: z.boolean().optional(),
    remains: z.enum(TILE_TYPE_IDS as [TileTypeId, ...TileTypeId[]]).optional(),
  })
  .strict();

// Key parity with the domain `ObjectProfile`: a field added on either side fails typecheck here
// until the other mirrors it, so an authored object can't silently lose a new profile field.
type ObjectProfileSchemaKeys = keyof z.infer<typeof ObjectProfileSchema>;
const objectProfileKeyParity: [ObjectProfileSchemaKeys, keyof ObjectProfile] extends [keyof ObjectProfile, ObjectProfileSchemaKeys] ? true : never = true;
void objectProfileKeyParity;

// ── the effect-atom vocabulary (closed, recursive via control-flow branches) ──────────

/**
 * The list of atom ops {@link EffectAtomSchema}'s discriminated union covers — the **schema's**
 * op vocabulary. `schema.test.ts` asserts this set-equals `IMPLEMENTED_ATOM_OPS` (the
 * interpreter's), so a new engine atom op surfaces as a loud parity failure. The `satisfies`
 * makes it a compile error to list an op that isn't a real {@link EffectAtom} discriminant.
 */
export const SCHEMA_ATOM_OPS = [
  "add_status",
  "reserve_followup",
  "remove_status",
  "remove_status_from_source",
  "pause_status_expiry",
  "add_resource",
  "set_resource",
  "grant_action_allowance",
  "grant_free_action_use",
  "extra_damage",
  "add_heat",
  "set_hp",
  // Grow the acting unit's move budget (Ace's Afterburners).
  "grant_move",
  "expend_charges",
  "use_cap",
  // Keep-highest Overshield and a Burn clear (Black Thumb's Field Repair and Extinguish).
  "overshield",
  "clear_burn",
  // Start and end a Black Thumb Rodeo (`rodeo.ts`).
  "begin_rodeo",
  "end_rodeo",
  "reveal",
  "heal",
  // `npc-features-fielded-roster` Phase 5.2 — a standalone, mitigated, cascade-integrated hit.
  "damage",
  "reduce_pending_damage",
  "split_pending_damage",
  "redirect_pending_damage",
  // `npc-features-fielded-roster` Phase 7.1 — NOT pre-extracted (see `atoms.ts`'s own doc): a
  // genuine member of the vocabulary that composes under `if`/`roll` (Rebound's "Use → roll 1d6 →
  // on 4+ resist").
  "resist_pending_damage",
  "forced_move",
  "move_self_toward_target",
  "move_up_to",
  "deploy_object",
  "deploy_mine",
  "recall_object",
  "redeploy_object",
  "despawn",
  "reload",
  "repair_object_group",
  // Create a standing area effect (a live positional cover query), splicing an `area_add` step.
  "create_area",
  "force_save",
  "skill_check",
  "roll",
  "if",
  "choose",
  "for_each",
  "choose_random",
  "choose_from_area",
  "place_adjacent",
  "teleport",
  "move_spaces",
  "destroy_system",
  "forced_attack",
  "contested_roll",
  "cause_miss",
  "builtin",
  // Surface an authored GM-facing warning verbatim (Impale's un-automated pull-to-endpoint —
  // `npc-features-fielded-roster` Phase 6.2).
  "advisory",
] as const satisfies readonly EffectAtom["op"][];

/** A {@link import("./atoms").RodeoStatusSpec}: a status a rodeo plants beside its markers. */
const RodeoStatusSpecSchema = z.object({ status: z.string().min(1), until: StatusExpirySchema.optional() }).strict();

/**
 * The {@link EffectAtom} schema. Grounded atoms map ~1:1 to a step; control-flow atoms
 * (`force_save`/`roll`/`if`/`choose`/`for_each`) recurse into nested atom lists (hence `z.lazy`).
 */
export const EffectAtomSchema: z.ZodTypeAny = z.lazy(() =>
  z.discriminatedUnion("op", [
    // grounded atoms
    z
      .object({
        op: z.literal("add_status"),
        status: z.string(),
        until: StatusExpirySchema.optional(),
        endsOn: z.array(z.object({ on: z.enum(["damage", "move"]) }).strict()).optional(),
        target: ActorRefSchema.optional(),
        // The paired-combatant ref (a grapple's two ends).
        partner: ActorRefSchema.optional(),
        // A literal item-identity override for the emitted instance's `source`, read by a
        // sibling `has_status` with `source: { id }` predicate elsewhere. See `AddStatusAtom.sourceOverride`'s doc.
        sourceOverride: z.string().optional(),
      })
      .strict(),
    // Plant a weapon-pinned follow-up reservation on the attacker (Executioner's Backswing
    // Cut). The data facets (source/partner/weapon-instance) come from the firing context, not
    // authored refs, so the arm carries only the reservation name + its expiry.
    z
      .object({
        op: z.literal("reserve_followup"),
        status: z.string(),
        until: StatusExpirySchema.optional(),
      })
      .strict(),
    z.object({ op: z.literal("remove_status"), status: z.string(), target: ActorRefSchema.optional(), exceptSource: ActorRefSchema.optional() }).strict(),
    // The source-scoped CROSS-TARGET remove: clears every combatant's instance of `status`
    // whose live `source` is the acting combatant (self). Deliberately has no `scope` widening
    // — a roster-wide reading isn't needed; a per-target one is a plain, source-blind
    // `remove_status { target: "target" }`. See that atom's own doc.
    z.object({ op: z.literal("remove_status_from_source"), status: z.string() }).strict(),
    // pause_status_expiry (ms_stay_of_execution's time-freeze clause): suspend/resume a named
    // status instance's expiry timer via one atom + a boolean.
    z
      .object({
        op: z.literal("pause_status_expiry"),
        status: z.string(),
        target: ActorRefSchema.optional(),
        suspended: z.boolean().optional(),
      })
      .strict(),
    z
      .object({ op: z.literal("add_resource"), key: z.string(), delta: NumberOrTemplate, target: ActorRefSchema.optional(), max: NumberOrTemplate.optional() })
      .strict(),
    z
      .object({ op: z.literal("set_resource"), key: z.string(), val: NumberOrTemplate, target: ActorRefSchema.optional() })
      .strict(),
    // t_executioner:1's on-hit bonus-Skirmish grant (Backswing Cut) and Everest's
    // free-quick-action grants, as a triggerable DSL op rather than a hardcoded id set.
    z
      .object({
        op: z.literal("grant_action_allowance"),
        actionId: z.string(),
        amount: NumberOrTemplate.optional(),
        who: ActorRefSchema.optional(),
      })
      .strict(),
    // The per-verb cost earmark. `verb` is a CLOSED enum of RAW verb names (`EARMARK_VERBS`),
    // not a free string: an earmark naming a verb nothing can match is silently dead rather
    // than loudly wrong, so a typo must fail to PARSE. This is also why it is not the
    // ledger-id space — that space collapses Scan/Lock On/Bolster to `"quick_tech"`.
    z
      .object({
        op: z.literal("grant_free_action_use"),
        verb: z.enum(EARMARK_VERBS),
        amount: NumberOrTemplate.optional(),
        who: ActorRefSchema.optional(),
      })
      .strict(),
    z
      .object({
        op: z.literal("extra_damage"),
        // `MagnitudeOrTemplate`, not a bare `NumberOrTemplate` (the area fan-out tock): a rider's
        // flat magnitude may scale off the carrier's frozen tier, a frozen stat, or the firing
        // attack's TARGET COUNT (`{ targetCount: "beyond_first", mult: { tier: [2,3,4] } }` —
        // Cluster Munitions). Same widening `add_heat`/`heal`/`damage` already carry.
        amount: MagnitudeOrTemplate.optional(),
        dice: z.object({ count: NumberOrTemplate, sides: NumberOrTemplate }).strict().optional(),
        half: z.boolean().optional(),
        // `area-bonus-damage-halving` Phase 1: the RAW "this IS printed BONUS DAMAGE" opt-in
        // (`ExtraDamageAtom.bonusDamage`'s own doc carries the full account, including why it
        // is not named `bonus`). This object is `.strict()`, so the mirror is mandatory: without
        // it every overlay entry carrying the flag fails `parseOverlayEntry` outright.
        bonusDamage: z.boolean().optional(),
        // BC-019: the entry prints AP, so standalone damage ignores Armor (`ExtraDamageAtom.ap`).
        ap: z.boolean().optional(),
        dtype: DamageTypeSchema.optional(),
        target: ActorRefSchema.optional(),
      })
      .strict(),
    // `amount` is a `MagnitudeOrTemplate` — a plain bound number/`{VAL}` template OR a
    // `StatScaledAmountSchema` (`ms_type_i_flight_system`'s "Size +1 Heat", resolved off the
    // acting combatant's frozen Size at interpret time).
    z
      .object({
        op: z.literal("add_heat"),
        amount: MagnitudeOrTemplate,
        target: ActorRefSchema.optional(),
        upTo: z.object({ key: z.string().min(1), max: z.number().int().positive() }).strict().optional(),
      })
      .strict(),
    z.object({ op: z.literal("set_hp"), val: NumberOrTemplate, target: ActorRefSchema.optional() }).strict(),
    z.object({ op: z.literal("grant_move"), cells: MagnitudeOrTemplate, who: ActorRefSchema.optional() }).strict(),
    z.object({ op: z.literal("expend_charges"), items: z.array(z.string().min(1)).min(1), count: z.number().int().positive() }).strict(),
    z.object({ op: z.literal("use_cap"), cap: CapSpecSchema, on: ActorRefSchema.optional() }).strict(),
    z.object({ op: z.literal("overshield"), value: z.number().int().positive(), target: ActorRefSchema.optional() }).strict(),
    z.object({ op: z.literal("clear_burn"), target: ActorRefSchema.optional() }).strict(),
    z.object({ op: z.literal("begin_rodeo"), rider_statuses: z.array(RodeoStatusSpecSchema).optional() }).strict(),
    z.object({ op: z.literal("end_rodeo"), lockout: RodeoStatusSpecSchema.optional() }).strict(),
    // Scan's reveal atom. `level` is NOT NumberOrTemplate (it's the 3-state disclosure enum,
    // not a magnitude) — mirrors
    // `DisclosureLevel` in `@lancer-vtt/domain` exactly (kept a literal enum here, not imported,
    // since `schema.ts` stays a pure runtime mirror of `atoms.ts`'s compile-time shapes).
    z
      .object({ op: z.literal("reveal"), level: z.enum(["class", "loadout", "full"]), target: ActorRefSchema.optional() })
      .strict(),
    // `amount` takes the SAME `MagnitudeOrTemplate` widening `add_heat` has — a plain bound
    // number OR a `StatScaledAmountSchema` (pg_patch's "half their maximum HP" is
    // `{ stat: "maxHp", who: "target", mult: 0.5 }`).
    z.object({ op: z.literal("heal"), amount: MagnitudeOrTemplate, target: ActorRefSchema.optional() }).strict(),
    // `npc-features-fielded-roster` Phase 5.2 — a standalone, MITIGATED, cascade-integrated
    // hit (`atoms.ts`'s `DamageAtom` own doc carries the full account of why this is NOT
    // `extra_damage`). `amount` is `MagnitudeOrTemplate` so Trample's `{tier:[2,3,4]}` binds.
    z
      .object({
        op: z.literal("damage"),
        amount: MagnitudeOrTemplate,
        dtype: DamageTypeSchema.optional(),
        ap: z.boolean().optional(),
        irreducible: z.literal(true).optional(),
        target: ActorRefSchema.optional(),
      })
      .strict(),
    z.object({ op: z.literal("destroy_system"), system: z.string().min(1), who: ActorRefSchema.optional() }).strict(),
    // Reaction-window-only atoms (see `atoms.ts`'s doc): the pending-damage reduce/split
    // transforms `gatherReaction` composes.
    z
      .object({
        op: z.literal("reduce_pending_damage"),
        dice: z.object({ count: NumberOrTemplate, sides: NumberOrTemplate }).strict(),
        bonus: NumberOrTemplate.optional(),
      })
      .strict(),
    z.object({ op: z.literal("split_pending_damage") }).strict(),
    z.object({ op: z.literal("redirect_pending_damage") }).strict(),
    z.object({ op: z.literal("resist_pending_damage") }).strict(),
    z
      .object({
        op: z.literal("forced_move"),
        dist: NumberOrTemplate,
        target: ActorRefSchema.optional(),
        away_from: ActorRefSchema.optional(),
        // The chosen-direction mode (`"chosen"`) or a pull toward a bound combatant (Magnetic
        // Cannon's "pulled … toward you"), or toward the area center (`"site"`, a mine's space).
        // A closed enum, so a hallucinated keyword still fails.
        toward: z.enum(["chosen", "self", "target", "attacker", "site", "pair"]).optional(),
        chooser: ActorRefSchema.optional(),
      })
      .strict(),
    // t_hunter:1's Lunge / t_ace:1's any-direction Acrobatics. `upTo`/`as` are the "up to N,
    // flying" magnitude pick + per-walk flight override (`atoms.ts`'s
    // `MoveSelfTowardTargetAtom` doc carries the full rationale — this schema stays a faithful
    // runtime mirror of that type).
    z
      .object({
        op: z.literal("move_self_toward_target"),
        // A `MagnitudeValue`, not just `NumberOrTemplate` (`npc-features-fielded-roster`
        // Phase 3.3) — `{ stat: "speed" }` reads the mover's own frozen Speed
        // (Charge/Avalanche Charge), resolved at interpret time off the resolved `who`.
        dist: MagnitudeOrTemplate,
        who: ActorRefSchema.optional(),
        toward: ActorRefSchema.optional(),
        upTo: z.literal(true).optional(),
        as: z.literal("flying").optional(),
        provokesReactions: z.literal(true).optional(),
        chooser: ActorRefSchema.optional(),
        asMove: z.literal(true).optional(),
        involuntary: z.literal(true).optional(),
      })
      .strict(),
    // Content-driven deploy onto the `spawnObjectGroup` helper
    // (ms_pattern_a_jericho_deployable_cover's "Deploy two sections of Size 1 hard cover…").
    z
      .object({
        op: z.literal("deploy_object"),
        name: z.string(),
        sectionCount: NumberOrTemplate,
        kind: z.enum(["object", "deployable", "drone"]).optional(),
        placement: z.literal("sensors").optional(),
        armor: NumberOrTemplate.optional(),
        // Per-call size/evasion/edef overrides (ms_turret_drones' Size 0.5 / 10 Evasion / 10
        // Edef), against `spawnObjectGroup`/`spawnObject`'s flat defaults (Size 1 / 5 Evasion
        // / 0 Edef).
        size: NumberOrTemplate.optional(),
        evasion: NumberOrTemplate.optional(),
        edef: NumberOrTemplate.optional(),
        objectProfile: ObjectProfileSchema.optional(),
        near: ActorRefSchema.optional(),
        groupId: z.string().min(1),
        within: z
          .object({ shape: z.literal("disk"), size: z.number().int().nonnegative(), origin: z.enum(["self", "target", "site"]) })
          .strict()
          .optional(),
        repeatable: z.literal(true).optional(),
        aura: z
          .object({
            radius: z.number().int().positive(),
            facets: z.array(z.enum(["ally_soft_cover", "deny_invisible", "deny_hide"])).min(1).optional(),
          })
          .strict()
          .optional(),
        statuses: z.array(z.union([z.string().min(1), RodeoStatusSpecSchema])).min(1).optional(),
      })
      .strict(),
    // Mint a plain mine Combatant via the `spawnMine` engine adapter (a mine IS a Combatant,
    // so this reuses `spawn_object`, not a new step). `damage` mirrors `MineProfile.damage` (a
    // flat number or a rolled `{count,sides}` dice spec — HEX's Explosive Mine is `2d6`) and is
    // OPTIONAL, since the Smoke Mine authors no damage at all. `damageType` is ALSO optional —
    // required only when `damage`/`objectDamage` is authored, which `spawnMine` enforces, not
    // this schema. `trigger`/`areaGrants` mirror `MineProfile.trigger`/`MineProfile.areaGrants`
    // exactly — narrow literal unions, not widened past what the engine accepts, so an
    // unauthorable value is a validation error here rather than a silent runtime surprise.
    // `saveDc` is deliberately absent — it comes from `EffectContext.defaultSaveDc`, not
    // authored content. A mine authoring NONE of
    // `damage`/`objectDamage`/`areaGrants`, or authoring `damage`/`objectDamage` with no
    // `damageType`, both parse fine here (cross-field content rules don't belong in this
    // shape-only schema) and are rejected at `spawnMine`'s own construction-time guards
    // instead, which `interpretDeployMine` already surfaces as an honest no-op with a
    // deferral warning — see that function's doc for why this is deliberate, not deferred.
    z
      .object({
        op: z.literal("deploy_mine"),
        name: z.string(),
        burstRadius: NumberOrTemplate,
        damage: z.union([NumberOrTemplate, z.object({ count: NumberOrTemplate, sides: NumberOrTemplate }).strict()]).optional(),
        damageType: DamageTypeSchema.optional(),
        saveAxis: z.string().optional(),
        ap: z.boolean().optional(),
        objectDamage: NumberOrTemplate.optional(),
        trigger: z.enum(["ally", "remote", "flying"]).optional(),
        areaGrants: z.literal("soft_cover").optional(),
        single_target: z.literal(true).optional(),
        on_fail: z.array(EffectAtomSchema).optional(),
        on_pass: z.array(EffectAtomSchema).optional(),
        reach: z.literal("over").optional(),
        catches: z.literal("detonator").optional(),
        expires: z.literal("deployer_turn_start").optional(),
        leaves: z.object({ name: z.string(), hp: z.number().int().positive(), evasion: z.number().int() }).strict().optional(),
        arm_now: z.literal(true).optional(),
        visible: z.literal(true).optional(),
        count: z.number().int().positive().optional(),
        placement: z.union([z.literal("sensors"), z.object({ within: z.number().int().positive() }).strict()]).optional(),
        near: ActorRefSchema.optional(),
        groupId: z.string().min(1),
      })
      .strict(),
    // The inverse of `deploy_object`: despawn an already-deployed group, freeing it for redeploy.
    z
      .object({
        op: z.literal("recall_object"),
        groupId: z.string().min(1),
        near: ActorRefSchema.optional(),
      })
      .strict(),
    // Move a deployed drone to a new space (a redeploy), by the deploy's own placement rule.
    z
      .object({
        op: z.literal("redeploy_object"),
        groupId: z.string().min(1),
        near: ActorRefSchema.optional(),
        placement: z.literal("sensors").optional(),
      })
      .strict(),
    // Take a deployed drone off the field without destroying it (Restock's disintegration).
    z.object({ op: z.literal("despawn"), target: ActorRefSchema.optional() }).strict(),
    z.object({ op: z.literal("move_up_to"), target: ActorRefSchema.optional(), dist: z.number().int().positive() }).strict(),
    // Reload one of the target's unloaded Loading weapons (Restock).
    z.object({ op: z.literal("reload"), pick: z.literal("one"), target: ActorRefSchema.optional() }).strict(),
    // Spend a Repair, scoped to the carrying system, to restore a deployed group's Destroyed
    // sections.
    z
      .object({
        op: z.literal("repair_object_group"),
        groupId: z.string().min(1),
        near: ActorRefSchema.optional(),
        cost: NumberOrTemplate.optional(),
      })
      .strict(),
    // Create a standing area effect on the map (a thrown Smoke Grenade's Blast cloud).
    // `origin` mirrors AreaSelectorSchema's own site/self/target vocabulary; `shape`/`grants`
    // are the SAME narrow fields AreaEffect itself carries (do not widen speculatively);
    // `until` mirrors add_status's StatusExpiry; `source` is independently settable, defaulting
    // to the activator.
    z
      .object({
        op: z.literal("create_area"),
        origin: z.enum(["self", "target", "site", "site_pair"]),
        pairWithin: z.number().int().positive().optional(),
        shape: z.literal("disk"),
        size: NumberOrTemplate,
        grants: z.enum(["soft_cover", "outside_attack_difficulty", "blind_out", "no_reactions", "none"]),
        until: StatusExpirySchema,
        source: ActorRefSchema.optional(),
        replace: z.literal(true).optional(),
      })
      .strict(),
    // control-flow atoms (recurse)
    z
      .object({
        op: z.literal("force_save"),
        stat: z.string(),
        dc: NumberOrTemplate.optional(),
        actor: ActorRefSchema.optional(),
        // `save_cause` (ms_stable_structure's Prone/Knockback scope).
        cause: z.string().optional(),
        // A per-invocation accuracy/difficulty shift on THIS save's own roll (Impale's "+1
        // Difficulty" — `npc-features-fielded-roster` Phase 6.2). See `atoms.ts`'s doc.
        accuracy: z.number().optional(),
        on_fail: z.array(EffectAtomSchema).optional(),
        on_pass: z.array(EffectAtomSchema).optional(),
      })
      .strict(),
    z
      .object({
        op: z.literal("skill_check"),
        skill: z.enum(["hull", "agility", "systems", "engineering"]),
        dc: z.number().optional(),
        on_pass: z.array(EffectAtomSchema).optional(),
        on_fail: z.array(EffectAtomSchema).optional(),
      })
      .strict(),
    z
      .object({
        op: z.literal("roll"),
        count: NumberOrTemplate,
        sides: NumberOrTemplate,
        cmp: CmpOpSchema.optional(),
        keep: z.literal("highest").optional(),
        threshold: NumberOrTemplate,
        bind: z.string().optional(),
        on_hit: z.array(EffectAtomSchema).optional(),
        on_miss: z.array(EffectAtomSchema).optional(),
      })
      .strict(),
    z
      .object({
        op: z.literal("if"),
        cond: PredicateSchema,
        then: z.array(EffectAtomSchema),
        else: z.array(EffectAtomSchema).optional(),
      })
      .strict(),
    z
      .object({
        op: z.literal("choose"),
        prompt: z.string(),
        options: z.array(
          z.object({ id: z.string(), label: z.string().optional(), when: PredicateSchema.optional(), effect: z.array(EffectAtomSchema) }).strict(),
        ),
        // `choose_target`'s actor-routing. Omitted → "attacker".
        actor: z.enum(["attacker", "target"]).optional(),
        extend: z.literal("invade_options").optional(),
      })
      .strict(),
    z
      .object({
        op: z.literal("for_each"),
        in: AreaSelectorSchema,
        as: z.enum(["target", "partner"]),
        effect: z.array(EffectAtomSchema),
      })
      .strict(),
    z
      .object({
        op: z.literal("choose_random"),
        in: AreaSelectorSchema,
        as: z.literal("target"),
        effect: z.array(EffectAtomSchema),
      })
      .strict(),
    z
      .object({
        op: z.literal("choose_from_area"),
        prompt: z.string().min(1),
        in: AreaSelectorSchema,
        as: z.enum(["target", "partner"]),
        include_self: z.literal(true).optional(),
        count: z.literal("any").optional(),
        effect: z.array(EffectAtomSchema),
      })
      .strict(),
    z
      .object({
        op: z.literal("place_adjacent"),
        target: ActorRefSchema.optional(),
        to: ActorRefSchema.optional(),
      })
      .strict(),
    z
      .object({
        op: z.literal("move_spaces"),
        dist: MagnitudeOrTemplate,
        who: ActorRefSchema.optional(),
        engagement: z.literal("stops").optional(),
      })
      .strict(),
    z
      .object({
        op: z.literal("teleport"),
        // `dist` (a pick within range) xor `swap` (trade places); the refine below the union enforces it.
        dist: MagnitudeOrTemplate.optional(),
        swap: ActorRefSchema.optional(),
        who: ActorRefSchema.optional(),
        from: ActorRefSchema.optional(),
        chooser: ActorRefSchema.optional(),
        optional: z.literal(true).optional(),
        involuntary: z.literal(true).optional(),
      })
      .strict(),
    z
      .object({
        op: z.literal("forced_attack"),
        weapon: z.union([
          z.string(),
          z
            .object({
              pick: z
                .object({ mount: z.string().optional(), ranged: z.boolean().optional(), reach: z.enum(["range", "threat"]) })
                .strict(),
            })
            .strict(),
        ]),
        target: ActorRefSchema.optional(),
        attacker: ActorRefSchema.optional(),
        reactions: z.literal(false).optional(),
        consume: z.literal(false).optional(),
        autoHit: z.literal(true).optional(),
        secondary: z.literal(true).optional(),
      })
      .strict(),
    z
      .object({
        op: z.literal("contested_roll"),
        vs: ActorRefSchema,
        kind: z.literal("ranged_attack"),
        on_win: z.array(EffectAtomSchema).optional(),
        on_lose: z.array(EffectAtomSchema).optional(),
      })
      .strict(),
    z.object({ op: z.literal("cause_miss") }).strict(),
    z.object({ op: z.literal("builtin"), id: z.string(), params: z.record(z.unknown()).optional() }).strict(),
    // Surface an authored GM-facing warning verbatim (Impale's un-automated pull-to-endpoint —
    // `npc-features-fielded-roster` Phase 6.2). See `atoms.ts`'s `AdvisoryAtom` doc.
    z.object({ op: z.literal("advisory"), message: z.string() }).strict(),
  ]).superRefine((atom, ctx) => {
    if (atom.op === "teleport" && (atom.dist === undefined) === (atom.swap === undefined)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: "teleport takes exactly one of `dist` (a pick within range) or `swap` (trade places)" });
    }
    if (atom.op === "create_area" && (atom.origin === "site_pair") !== (atom.pairWithin !== undefined)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: "create_area takes `pairWithin` exactly when `origin` is \"site_pair\"" });
    }
    if (atom.op === "teleport" && atom.swap !== undefined && (atom.from ?? atom.chooser ?? atom.optional ?? atom.involuntary) !== undefined) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: "a `swap` teleport takes no `from`, `chooser`, `optional` or `involuntary`" });
    }
  }),
);

// ── the firing-profile selector (carried on a `select` effect, NOT an EffectAtom arm) ─

/** The {@link SelectProfileAtom} — carried by a `select` effect, not a runtime atom. */
const SelectProfileSchema = z
  .object({
    op: z.literal("select_profile"),
    resource: z.string().optional(),
    status: z.string().optional(),
    who: ActorRefSchema.optional(),
    tiers: z.array(z.object({ at: NumberOrTemplate, profile: z.string() }).strict()),
    fallback: z.string().optional(),
  })
  .strict()
  .refine((s) => (s.resource === undefined) !== (s.status === undefined), { message: "a `select_profile` reads exactly one of `resource` or `status`" });

// ── the immunity facet ────────────────────────────────────────────────────────────────

/** The {@link ImmunityScope} union — mirrors `atoms.ts` exactly (one arm per `kind`). */
const ImmunityScopeSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("tech"), except: z.array(z.string()).optional() }).strict(),
  z.object({ kind: z.literal("damage"), types: z.array(DamageTypeSchema).optional() }).strict(),
  z.object({ kind: z.literal("status"), name: z.string(), from: z.literal("smaller_character").optional() }).strict(),
  // Heavy Frame's "can't be pushed, pulled … by smaller characters". Always source-gated.
  z.object({ kind: z.literal("forced_move"), from: z.literal("smaller_character").optional() }).strict(),
  z.object({ kind: z.literal("all") }).strict(),
  // "All damage and effects" WITHOUT `all`'s movement/targeting control-axis grant. See
  // atoms.ts's ImmunityScope doc.
  z.object({ kind: z.literal("damage_and_effects") }).strict(),
]);

// ── modifier_consume_resource_on_apply ────────────────────────────────────────────────

/** The {@link import("./atoms").ResourceCap} shape — mirrors `atoms.ts` exactly. */
const ResourceCapSchema = z.object({ key: z.string().min(1), max: NumberOrTemplate, per: z.enum(["round", "own_turn", "turn", "scene"]).optional() }).strict();

/**
 * The {@link import("./atoms").LimitedUses} shape — the `action` clause's authored
 * Full-Repair-grain usage cap. `max` is a plain positive INTEGER, not {@link NumberOrTemplate}:
 * this cap is read at spawn (`freezeOverlayAffordances`), where no `{VAL}` tag binding exists, so a
 * template would freeze a nonsense cap silently. `grain`/`limited` are the facet's fixed
 * semantics rather than authored fields — `.strict()` is what refuses an author reaching for a
 * cadence this facet cannot deliver. See `atoms.ts`'s `LimitedUses` doc.
 */
const LimitedUsesSchema = z.object({ max: z.number().int().positive(), pool: z.string().min(1).optional() }).strict();

// ── the modifier contribution ─────────────────────────────────────────────────────────

const ModifierContributionSchema = z
  .object({
    attackerAccuracy: NumberOrTemplate.optional(),
    // Scopes the attack-resolution facets to a single attack verb (t_juggernaut:1's "your next Ram").
    // Mirrors `saveScope`'s shape; see atoms.ts's `ModifierContribution.attackScope` doc — this
    // is the attack-verb space (`ATTACK_VERBS`), NOT `AttackMatch.actionId`'s composite carrier-id space.
    attackScope: z.object({ verb: AttackVerbSchema }).strict().optional(),
    targetAccuracy: NumberOrTemplate.optional(),
    consumeOnHit: z.boolean().optional(),
    ignoreTargetArmor: z.boolean().optional(),
    doubleDamage: z.boolean().optional(),
    saveCheckBonus: NumberOrTemplate.optional(),
    // The save-scope facet: scopes saveCheckBonus/consumeOnSaveCheck by the save's authored
    // `cause` (ms_stable_structure) and/or its HASE `stat` (t_ace:1); both present ANDs
    // together. Mirrors `atoms.ts`'s `ModifierContribution.saveScope`.
    saveScope: z.object({ cause: z.string().optional(), stat: z.string().optional(), roll: z.enum(["save", "check"]).optional() }).strict().optional(),
    consumeOnSaveCheck: z.boolean().optional(),
    advisory: z.string().optional(),
    immunity: z.array(ImmunityScopeSchema).optional(),
    // Mirrors `atoms.ts`'s `DamageType[] | "all"` shape (Argonaut Shield authors
    // `resistance: "all"`).
    resistance: z.union([z.literal("all"), z.array(DamageTypeSchema)]).optional(),
    spatialRiderDeferred: z.string().optional(),
    tag: z.object({ name: z.string(), val: NumberOrTemplate.optional() }).strict().optional(),
    // Stunned's Evasion cap (target-side) + auto-fail HULL/AGI (carrier-self, free stat-name
    // strings — the same space `force_save`'s `stat` and a contest axis label use).
    evasionCap: NumberOrTemplate.optional(),
    autoFailChecks: z.array(z.string()).optional(),
    // The shared "1/round" cap facet (vanguard's CQB Range-3 Accuracy, tactician's Opportunist).
    consumeResourceOnApply: ResourceCapSchema.optional(),
    // Walking Armory's SABOT: the attack gains AP (attacker-side).
    armorPiercing: z.literal(true).optional(),
    // Walking Armory's HELLFIRE: the attack's bonus damage takes this type (attacker-side).
    bonusDamageType: DamageTypeSchema.optional(),
    // t_walking_armory:1's per-attack damage-type override (attacker-side).
    overrideDamageType: DamageTypeSchema.optional(),
    // Vanguard 2: the attack ignores cover (attacker-side).
    ignoreCover: z.boolean().optional(),
    // Sniper's Mark's "deals 1 structure damage instead of its usual damage" (target-side).
    // Landed in the SAME commit as its `atoms.ts` field, per the Phase 7.4 `DamageMatch.ranged`
    // lesson: a hand-built `OverlayEntry` test fixture never asks this schema a question, so a TS
    // widening without its zod twin stays invisible until a real sidecar entry is validated.
    substituteStructureDamage: z.boolean().optional(),
    // The terraforming rig's Unarmed "cannot make attacks" (carrier-self).
    cannotAttack: z.literal(true).optional(),
    // The Goliath's Guardian: hard cover for allied characters (carrier-self).
    coverToAllies: z.literal("hard").optional(),
    // Markerlight's mark: the carrier can't benefit from soft cover (carrier-self).
    deniesSoftCover: z.literal(true).optional(),
    // Skirmisher 1 / Combined Arms 1: the carrier counts as having soft cover (carrier-self).
    softCover: z.literal(true).optional(),
    // Sea Legs: the carrier picks where someone else's push takes it, 1/round (carrier-self).
    chooseInvoluntaryHeading: z.literal(true).optional(),
    // Siege Stabilizers: +N range on ranged attacks, no ranged attack within N, no melee (carrier-self).
    rangeBonus: z.number().int().positive().optional(),
    minimumRange: z.number().int().positive().optional(),
    cannotMelee: z.literal(true).optional(),
    // Synthetic Muscle Netting: may Ram a larger target (carrier-self).
    ramsLarger: z.literal(true).optional(),
    // Black Thumb 1: a standing re-price of one core action (carrier-self).
    actionCost: z.object({ action: z.string().min(1), activation: z.enum(["quick", "free"]) }).strict().optional(),
    // Shatterhead: the weapon's own attack roll ignores Difficulty (weapon-own, read by the to-hit).
    ignoreDifficulty: z.literal(true).optional(),
    // The Fusion Rifle's distance damage and the Unraveler's Reliable-unless-structure (weapon-own, read by the attack spine).
    damageEqualsDistance: z.object({ max: z.number().int().positive() }).strict().optional(),
    reliableUnlessStructure: z.literal(true).optional(),
    // Multi-target weapons: Sharanga/Combat Sheathe's N at a time, the Impact Lance's line; the Sheathe's Jammed use (weapon-own).
    attacksTargets: z.number().int().min(2).optional(),
    lineToTarget: z.object({ heatPerExtra: z.number().int().nonnegative() }).strict().optional(),
    usableWhileJammed: z.literal(true).optional(),
    // The Veil Rifle: allies caught in the area aren't attacked; `effect` runs on each (weapon-own).
    sparesAllies: z.object({ effect: z.array(EffectAtomSchema) }).strict().optional(),
    // The Magnetic Cannon: once the attack hits anyone, `effect` runs on every character in the area (weapon-own).
    areaOnHit: z.object({ effect: z.array(EffectAtomSchema) }).strict().optional(),
  })
  .strict();

// ── the effect taxonomy (the `kind`-discriminated clause) ─────────────────────────────

const ModifierEntrySchema = z
  .object({
    kind: z.literal("modifier"),
    condition: PredicateSchema.optional(),
    // The event-fact gate beside `condition` (`atoms.ts`'s `ModifierEffect.match`). Always the
    // attack-family match (a modifier only ever folds during an attack resolution) — never a
    // discriminated union over every family the way a `triggered` clause's `match` is.
    // `verb` is omitted: the modifier fold's event carries no verb, so it could never match and
    // the clause would silently never fold. `mod.attackScope` scopes a modifier to a verb.
    match: AttackMatchSchema.omit({ verb: true }).optional(),
    mod: ModifierContributionSchema,
    // Who a carrier's modifier reaches (Combined Arms/Spotter): absent = carrier-only
    // (default); "adjacent_allies" = additive self+allies; "adjacent_allies_only" =
    // ally-exclusive, never the carrier; "bound_partner" = ally-exclusive to the ONE designated
    // Bondmate partner (t_bonded:1) — see atoms.ts's doc for full semantics.
    confer: z.enum(["adjacent_allies", "adjacent_allies_only", "bound_partner"]).optional(),
  })
  .strict();

const TRIGGERED_SHARED_FIELDS = {
  condition: PredicateSchema.optional(),
  optional: z.boolean().optional(),
  // A self-optional "may" rider (a pre-declared checkbox), gated in `fireTrigger` ABOVE the cap
  // check. A DIFFERENT control axis from `optional` above — never widen `optional` to cover
  // this; see `atoms.ts`'s `TriggeredEffect.requiresOptIn` doc for the full mechanism. Requires
  // `name`, enforced by the `requires-optin-needs-name` lint rule (`lint.ts`), not here: this
  // schema has no cross-field validation, only per-field shape.
  requiresOptIn: z.literal(true).optional(),
  // The authored initial checkbox state. Governs the UI pre-fill only; the engine stays
  // fail-closed (see `atoms.ts`'s `TriggeredEffect.optInDefault` doc for the full
  // layer-separation warning). REQUIRED whenever `requiresOptIn` is set — enforced by the
  // `requires-optin-needs-default` lint rule (`lint.ts`), not here, for the same reason
  // `requiresOptIn`'s own `name` requirement is.
  optInDefault: z.boolean().optional(),
  // The hit-side reaction that turns the triggering hit into a miss (Barrel Roll); see
  // `atoms.ts`'s `TriggeredEffect.causesMiss`.
  causesMiss: z.literal(true).optional(),
  priority: NumberOrTemplate.optional(),
  // The protector-class on_damage_taken handler marker.
  protector: z.boolean().optional(),
  // A reaction offered to a third combatant on someone else's attack; see `atoms.ts`'s
  // `TriggeredEffect.bystander` and `alsoSpends`.
  bystander: z.literal(true).optional(),
  alsoSpends: z.array(z.string()).optional(),
  // The reaction's own named-cap key (Intercede: "intercede"). Dual-purposed as the
  // `requiresOptIn` checkbox's answer key (`"optIn:" + name`) — see `atoms.ts`'s
  // `TriggeredEffect.name` doc.
  name: z.string().optional(),
  // A reaction with no named 1/round cap (Parry); see `atoms.ts`'s `TriggeredEffect.frequency`.
  frequency: z.literal("unlimited").optional(),
  // The shared "1/round" cap facet (centimane's on-crit save-or-debuff).
  consumeResourceOnApply: ResourceCapSchema.optional(),
  effect: z.array(EffectAtomSchema),
};

/**
 * The `triggered`-clause shape — `on: TriggerFamily` + optional per-family `match`. `atoms.ts`'s
 * `TriggeredEffect` carries only this shape; every pack entry + seed entry ships it natively.
 */
const TriggeredEntrySchema = z
  .object({
    kind: z.literal("triggered"),
    on: TriggerFamilySchema,
    match: TriggerMatchSchema.optional(),
    ...TRIGGERED_SHARED_FIELDS,
  })
  .strict()
  // A field acts at several moments; an `on:"aura"` clause with no moment would fire at all of them.
  .refine((c) => c.on !== "aura" || c.match?.family === "aura", { message: 'an on:"aura" clause needs match: { family: "aura", moment }', path: ["match"] });

/** One {@link EffectEntry} clause: modifier/triggered/action/select/invade_option, `kind`-tagged. */
export const EffectEntrySchema: z.ZodTypeAny = z.union([
  ModifierEntrySchema,
  TriggeredEntrySchema,
  z
    .object({
      kind: z.literal("action"),
      activation: z.enum(["quick", "quick_tech", "full", "protocol", "reaction", "free"]),
      condition: PredicateSchema.optional(),
      // A named clause is its own action, `<entryId>#<name>` (`atoms.ts`).
      name: z.string().min(1).optional(),
      // The Full-Repair-grain usage cap (`ms_custom_paint_job`'s one charge). Action-clause-only
      // and NOT interchangeable with the `triggered`/`modifier` clauses' per-ROUND
      // `consumeResourceOnApply` — see `atoms.ts`'s `LimitedUses` doc for why the two cadences
      // must not be conflated.
      limitedUses: LimitedUsesSchema.optional(),
      // A prose reach, frozen onto `actionRanges` at spawn (`atoms.ts`'s `ActionEffect.reach`).
      reach: z.union([z.number().int().positive(), z.literal("sensors")]).optional(),
      // A tech action whose activation word can't say so (`atoms.ts`'s `ActionEffect.tech`).
      tech: z.literal(true).optional(),
      // `false` ⇒ firing this action never discharges the carrier's Recharge (`atoms.ts`).
      spendsRecharge: z.literal(false).optional(),
      // An action a deployed drone carries for its allies (`atoms.ts`'s `ActionEffect.usableBy`).
      usableBy: z.object({ side: z.enum(["ally", "owner"]), within: z.number().int().min(0) }).strict().optional(),
      // A reaction with no named 1/round cap (`atoms.ts`'s `ActionEffect.frequency`).
      frequency: z.literal("unlimited").optional(),
      effect: z.array(EffectAtomSchema),
    })
    .strict()
    .refine((c) => c.usableBy === undefined || c.name !== undefined, { message: "a usableBy action clause needs a name: it is activated as <entryId>#<name>", path: ["name"] }),
  z.object({ kind: z.literal("select"), select: SelectProfileSchema }).strict(),
  // A roll replacement (`atoms.ts`'s `RollReplacementEffect`): an attack or damage roll rewritten before it is drawn.
  z
    .object({
      kind: z.literal("replacement"),
      name: z.string().min(1),
      on: z.literal("roll"),
      roll: z.enum(["attack", "damage"]),
      match: AttackMatchSchema.optional(),
      condition: PredicateSchema.optional(),
      confer: z.enum(["adjacent_allies", "adjacent_allies_only", "bound_partner"]).optional(),
      consumeResourceOnApply: ResourceCapSchema.optional(),
      instead: z.union([z.object({ op: z.literal("advantage") }).strict(), z.object({ op: z.literal("maximize") }).strict()]),
    })
    .strict(),
  // An attempt replacement (`atoms.ts`'s `AttemptReplacementEffect`): Invisible's coin.
  z
    .object({
      kind: z.literal("replacement"),
      name: z.string().min(1),
      on: z.literal("attack_attempt"),
      condition: PredicateSchema.optional(),
      confer: z.enum(["adjacent_allies", "adjacent_allies_only", "bound_partner"]).optional(),
      instead: z.object({ op: z.literal("miss_chance"), chance: z.number().gt(0).lte(1) }).strict(),
    })
    .strict(),
  // A check replacement (`atoms.ts`'s `CheckReplacementEffect`): the roller is offered a reroll.
  z
    .object({
      kind: z.literal("replacement"),
      name: z.string().min(1),
      on: z.literal("check"),
      check: z.enum(["save_check", "overheating"]),
      instead: z.object({ op: z.literal("reroll"), perRound: z.literal(true).optional() }).strict(),
    })
    .strict(),
  // A cost replacement (`atoms.ts`'s `CostReplacementEffect`).
  z
    .object({
      kind: z.literal("replacement"),
      name: z.string().min(1),
      on: z.literal("expend_charges"),
      match: z
        .object({
          own: z.literal(true).optional(),
          pool: z.string().min(1).optional(),
          firstAttackRoll: z.object({ hit: z.boolean().optional(), crit: z.boolean().optional() }).strict().optional(),
        })
        .strict()
        .optional(),
      instead: z.object({ op: z.literal("prevent") }).strict(),
    })
    .strict(),
  z
    .object({
      kind: z.literal("invade_option"),
      key: z.string().min(1),
      label: z.string().min(1),
      when: PredicateSchema.optional(),
      effect: z.array(EffectAtomSchema),
    })
    .strict(),
]);

// ── the overlay entry (the authored artifact the gate validates) ──────────────────────

/**
 * The schema for one authored {@link OverlayEntry} — **the hard gate**. An author's structured
 * output is forced through this before the orchestrator writes it. `id` must be a non-empty
 * string (it keys the file-per-entity write); `effects` is a list of validated clauses.
 */
export const OverlayEntrySchema: z.ZodTypeAny = z
  .object({
    id: z.string().min(1),
    effects: z.array(EffectEntrySchema),
    // The `ruled-narrative` marker (`RuledNarrative`). Optional and additive, so every
    // pre-marker entry still validates; both strings are required WHEN PRESENT and
    // non-blank, because a bare or uncited marker recreates the ambiguity the marker
    // exists to remove. `.trim().min(1)` rejects `"   "` as well as `""`.
    ruledNarrative: z
      .object({ reason: z.string().trim().min(1), ruling: z.string().trim().min(1) })
      .strict()
      .optional(),
  })
  .strict();

/** The discriminated result of running the gate — typed `OverlayEntry` on success. */
export type GateResult =
  | { success: true; data: OverlayEntry }
  | { success: false; error: z.ZodError };

/**
 * Parse-validate an authored {@link OverlayEntry} (the gate). Returns a discriminated
 * result so the orchestrator can branch on `.success` without a throw (the bounded-retry path).
 * On success the value is runtime-validated, so it is cast to {@link OverlayEntry}.
 *
 * This is the OTHER JSON→`OverlayEntry` boundary (`sidecar.ts`'s `parseSidecar` is the
 * bulk-pack one) — single-entry callers reading a shipped sidecar file directly (entry tests'
 * own `loadShipped`-style helpers) route through here, not `parseSidecar`.
 */
export function parseOverlayEntry(raw: unknown): GateResult {
  const r = OverlayEntrySchema.safeParse(raw);
  if (r.success) return { success: true, data: r.data as OverlayEntry };
  return { success: false, error: r.error };
}
